import { Component, ChangeDetectionStrategy, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { Popover, PopoverModule } from "primeng/popover";
import { TooltipModule } from "primeng/tooltip";

import { DYNAMIC_FIELDS } from "../../constants/retention-workflow.constants";

@Component({
  selector: "app-dynamic-fields-popover",
  standalone: true,
  imports: [CommonModule, ButtonModule, PopoverModule, TooltipModule],
  template: `
    <p-popover #dynamicFieldsPopover>
      <div class="dynamic-fields-popover">
        <div class="popover-header">
          <h4>Available Dynamic Fields</h4>
          <button
            type="button"
            class="popover-close"
            (click)="dynamicFieldsPopover.hide()"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>
        <div class="popover-body">
          <p class="popover-description">
            Use these placeholders in your offer text to dynamically insert
            values:
          </p>
          <div class="fields-list">
            @for (field of dynamicFields; track field.variable) {
              <div class="field-item">
                <code class="field-variable">{{ field.variable }}</code>
                <span class="field-description">{{ field.description }}</span>
              </div>
            }
          </div>
        </div>
      </div>
    </p-popover>

    <button
      type="button"
      class="dynamic-fields-trigger"
      (click)="toggle($event)"
      pTooltip="View available dynamic fields"
      tooltipPosition="top"
    >
      <i class="pi pi-question-circle"></i>
      <span>What dynamic fields can I use?</span>
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      .dynamic-fields-trigger {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        background: none;
        border: none;
        padding: 0.375rem 0.5rem;
        font-size: 0.8125rem;
        color: #3b82f6;
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
          color: #2563eb;
        }

        i {
          font-size: 0.875rem;
        }
      }

      .dynamic-fields-popover {
        width: 380px;
        max-width: 90vw;

        .popover-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;

          h4 {
            margin: 0;
            font-size: 0.9375rem;
            font-weight: 600;
            color: #1e293b;
          }

          .popover-close {
            background: none;
            border: none;
            padding: 0.25rem;
            cursor: pointer;
            color: #64748b;
            transition: color 0.2s;

            &:hover {
              color: #1e293b;
            }
          }
        }

        .popover-body {
          padding: 1rem;

          .popover-description {
            margin: 0 0 0.75rem;
            font-size: 0.8125rem;
            color: #64748b;
            line-height: 1.4;
          }

          .fields-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;

            .field-item {
              display: flex;
              flex-direction: column;
              gap: 0.25rem;
              padding: 0.5rem;
              background: #f8fafc;
              border-radius: 4px;

              .field-variable {
                font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
                font-size: 0.8125rem;
                color: #7c3aed;
                background: #f3f0ff;
                padding: 0.125rem 0.375rem;
                border-radius: 3px;
                display: inline-block;
                width: fit-content;
              }

              .field-description {
                font-size: 0.75rem;
                color: #64748b;
                line-height: 1.4;
              }
            }
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFieldsPopoverComponent {
  @ViewChild("dynamicFieldsPopover") popover!: Popover;

  readonly dynamicFields = DYNAMIC_FIELDS;

  toggle(event: Event): void {
    this.popover.toggle(event);
  }
}
